import GridLayout, { WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { GripVertical } from 'lucide-react'
import { useBuilder } from './hooks/useBuilder'
import { BuilderBlockWrapper } from './BuilderBlockWrapper'
import { BuilderBlockMount } from './BuilderBlockMount'

/**
 * LayoutStrategy `grid` (lot L1) — renders the canvas `blocs` as a 2-D
 * react-grid-layout instead of the linear vertical stack.
 *
 * Why a JSX primitive : 2-D placement + width/height resize + collision /
 * compaction is intrinsically imperative DOM (react-grid-layout) — impossible in
 * declarative YAML. It **cannot reuse `DashboardGrid`** (the dashboard page's
 * grid primitive) because that one renders YAML *templates* (`options[type]` via
 * `<View load>`), whereas the canvas renders *builder_def units* through
 * `BuilderBlockMount` (edit affordances, apiData, per-unit DataSync). This
 * component keeps that exact mount so blocks stay editable in the grid.
 *
 * Position `{x,y,w,h}` is per-bloc structural metadata (`bloc.layout`), written
 * back by `setGridLayout` on drag/resize END (never mid-gesture → charts, being
 * value-stable, don't refetch while resizing). Drag is limited to a dedicated
 * top-left handle (`.builder-grid-handle`) so clicking a field edits it instead
 * of dragging; RGL's own resize handle sits bottom-right (`se`), clear of
 * `BuilderBlockWrapper`'s top-right controls.
 */
const Grid = WidthProvider(GridLayout)
const MARGIN = [24, 24]
const num = (v, d) => (Number.isFinite(v) ? v : d)

export const BuilderGridLayout = ({
  blocs,
  registryByType,
  objectContext,
  isEditMode,
  cols = 12,
  rowHeight = 34,
}) => {
  const { setGridLayout } = useBuilder()

  // Derive the RGL layout from each bloc's stored position, falling back to a
  // simple auto-placement for blocs added but not yet dragged.
  const layout = blocs.map((b, i) => {
    const p = b.layout || {}
    return {
      i: b.id,
      x: num(p.x, (i * 6) % cols),
      y: num(p.y, Math.floor(i / 2) * 10),
      w: num(p.w, 6),
      h: num(p.h, 9),
      minW: 2,
      minH: 2,
    }
  })

  return (
    <Grid
      className="layout"
      layout={layout}
      cols={cols}
      rowHeight={rowHeight}
      margin={MARGIN}
      isDraggable={isEditMode}
      isResizable={isEditMode}
      draggableHandle=".builder-grid-handle"
      resizeHandles={['se']}
      compactType="vertical"
      onDragStop={(l) => setGridLayout(l)}
      onResizeStop={(l) => setGridLayout(l)}
    >
      {blocs.map((b, i) => {
        const def = registryByType[b.type]
        return (
          // `isolate` : la poignée de déplacement (z-30) et les commandes du
          // bloc vivaient dans le contexte d'empilement RACINE, à égalité avec
          // la barre d'outils `sticky` du builder (z-30) — et plus bas dans le
          // DOM, donc elles passaient PAR-DESSUS elle au défilement.
          <div key={b.id} className="relative isolate overflow-hidden">
            {isEditMode && (
              <span
                className="builder-grid-handle absolute top-2 left-2 z-30 flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-gray-700 cursor-move shadow-sm"
                title="Déplacer"
                aria-hidden="true"
              >
                <GripVertical size={14} />
              </span>
            )}
            {def?.rjBuild ? (
              <BuilderBlockWrapper bloc={b} index={i} total={blocs.length}>
                <BuilderBlockMount key={def.version} bloc={b} def={def} objectContext={objectContext} />
              </BuilderBlockWrapper>
            ) : (
              <div className="p-4 text-xs text-red-500 bg-red-50 border border-red-200 rounded">
                Unknown block type: <code>{b.type}</code>
              </div>
            )}
          </div>
        )
      })}
    </Grid>
  )
}
